import { IconEye, IconTrash } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef, FilterFn } from "@tanstack/react-table";
import { Flex, Grid, Input, Select, Typography } from "antd";
import { useMemo, useState } from "react";
import { useRouteOrgSlug } from "@/common/artemis-operations/organization";
import { confirmDeleteResource } from "@/common/confirm";
import { type ArtemisTask, TaskStatus } from "@/common/model";
import { CustomIcon, DateField, IdField } from "../base";
import type { KanbanConfig } from "../kanban-board";
import type { ResponsiveTableProps } from "../responsive-table";
import { ResponsiveTableKanban } from "../responsive-table-kanban";
import { TableFilterBar } from "../table-filter-bar";
import {
	TaskStatusBadge,
	TaskStatusLabelMap,
	TaskVerificationStatusBadge,
} from "./task-badges";
import { TaskDetailsDrawer } from "./task-details";

const searchFilter: FilterFn<ArtemisTask> = (row, _columnId, value: string) => {
	const v = value.toLowerCase();
	return (
		row.original.name.toLowerCase().includes(v) ||
		(row.original.id as string).toLowerCase().includes(v)
	);
};

type TaskBoardColumn = "created" | "queued" | "working" | "done";

// Exhaustive, so a new TaskStatus fails typecheck until it is placed on the board.
const BOARD_COLUMN_BY_STATUS: Record<TaskStatus, TaskBoardColumn> = {
	CREATED: "created",
	UNKNOWN: "created",
	SUBMITTED: "queued",
	WAITING: "queued",
	WORKING: "working",
	// Paused mid-run on a human — still in flight; the card's badge shows why.
	INPUT_REQUIRED: "working",
	APPROVAL_REQUIRED: "working",
	COMPLETED: "done",
	FAILED: "done",
	CANCELLED: "done",
};

// Board columns in lifecycle order; each groups several statuses.
const KANBAN: KanbanConfig<ArtemisTask, TaskBoardColumn> = {
	columns: [
		{
			key: "created",
			title: <Typography.Text strong>Created</Typography.Text>,
		},
		{ key: "queued", title: <Typography.Text strong>Queued</Typography.Text> },
		{
			key: "working",
			title: <Typography.Text strong>Working</Typography.Text>,
		},
		{
			key: "done",
			title: <Typography.Text strong>Done</Typography.Text>,
			terminal: true,
		},
	],
	getColumnKey: (task) => BOARD_COLUMN_BY_STATUS[task.status],
	getRowKey: (task) => task.id as string,
	renderCard: (task) => (
		<Flex vertical gap={4}>
			<Flex justify="space-between" align="center" gap={8}>
				<Typography.Text strong ellipsis style={{ minWidth: 0 }}>
					{task.name}
				</Typography.Text>
				<TaskStatusBadge status={task.status} />
			</Flex>
			<DateField value={task.createdAt as string} />
		</Flex>
	),
};

interface TaskTableProps
	extends Omit<ResponsiveTableProps<ArtemisTask>, "columns"> {
	onDeleteTasks?: (tasks: ArtemisTask[]) => void;
	// Controlled server-side search/filter (owned by the route via usePagedList).
	search?: string;
	onSearchChange?: (v: string) => void;
	status?: string;
	onStatusChange?: (v: string | undefined) => void;
}

export function TaskTable({
	onDeleteTasks,
	loading,
	search,
	onSearchChange,
	status,
	onStatusChange,
	...props
}: TaskTableProps) {
	const navigate = useNavigate();
	const orgSlug = useRouteOrgSlug();
	const [currentTask, setCurrentTask] = useState<ArtemisTask | null>(null);
	const screens = Grid.useBreakpoint();
	const isCompact = !screens.lg;

	const columns: ColumnDef<ArtemisTask>[] = useMemo(
		() => [
			{
				accessorKey: "name",
				header: "Name",
				filterFn: searchFilter,
				cell: ({ row }) => (
					<Typography.Text ellipsis strong>
						{row.original.name}
					</Typography.Text>
				),
			},
			{
				accessorKey: "status",
				header: "Status",
				size: 140,
				enableSorting: true,
				filterFn: "equals",
				cell: ({ row }) => <TaskStatusBadge status={row.original.status} />,
			},
			{
				accessorKey: "verificationStatus",
				header: "Verification",
				size: 140,
				enableSorting: true,
				filterFn: "equals",
				cell: ({ row }) =>
					row.original.verificationStatus ? (
						<TaskVerificationStatusBadge
							status={row.original.verificationStatus}
						/>
					) : (
						"-"
					),
			},

			// {
			//   accessorKey: "project",
			//   header: "Project",
			//   size: 140,
			//   cell: ({ row }) => row.original.project?.name || "-",
			// },
			// {
			//   accessorKey: "agent",
			//   header: "Agent",
			//   size: 140,
			//   cell: ({ row }) => row.original.agent?.name || "-",
			// },

			{
				accessorKey: "id",
				header: "ID",
				size: 200,
				enableSorting: true,
				filterFn: searchFilter,
				enableHiding: true,
				cell: ({ row }) => (
					<div style={{ display: "flex", alignItems: "center" }}>
						<IdField id={row.original.id as string} />
					</div>
				),
			},
			{
				accessorKey: "createdAt",
				header: "Created",
				size: 120,
				enableSorting: true,
				cell: ({ row }) => (
					<DateField value={row.original.createdAt as string} />
				),
			},
		],
		[],
	);

	const confirmDelete = (tasks: ArtemisTask[]) =>
		confirmDeleteResource(() => onDeleteTasks?.(tasks));

	return (
		<>
			<ResponsiveTableKanban<ArtemisTask, TaskBoardColumn>
				kanban={KANBAN}
				loading={loading}
				columns={columns}
				initialSorting={[{ id: "createdAt", desc: true }]}
				rowActions={[
					{
						label: "View",
						icon: <CustomIcon icon={IconEye} />,
						onClick: (task) => setCurrentTask(task),
					},
					{
						label: "Delete",
						icon: <CustomIcon icon={IconTrash} />,
						danger: true,
						onClick: (task) => confirmDelete([task]),
					},
				]}
				compactRender={(task) => (
					<article
						onKeyDown={() =>
							navigate({
								to: `/${orgSlug}/workspaces/${task.workspaceId}/tasks/${task.id}` as any,
							})
						}
						onClick={() =>
							navigate({
								to: `/${orgSlug}/workspaces/${task.workspaceId}/tasks/${task.id}` as any,
							})
						}
						style={{
							display: "flex",
							justifyContent: "space-between",
							width: "100%",
						}}
					>
						<Typography.Text strong ellipsis>
							{task.name}
						</Typography.Text>
						<TaskStatusBadge status={task.status} />
					</article>
				)}
				filterBar={() => (
					<TableFilterBar isCompact={isCompact}>
						<Input.Search
							placeholder="Search by name or ID"
							allowClear
							style={{ width: isCompact ? "100%" : 220 }}
							value={search ?? ""}
							onChange={(e) => onSearchChange?.(e.target.value)}
							onSearch={(v) => onSearchChange?.(v)}
						/>
						<Select
							allowClear
							placeholder="Status"
							style={{ width: isCompact ? "100%" : 160 }}
							value={status ?? null}
							onChange={(v) => onStatusChange?.(v ?? undefined)}
							options={Object.values(TaskStatus).map((s) => ({
								value: s,
								label: TaskStatusLabelMap[s],
							}))}
						/>
					</TableFilterBar>
				)}
				onRowClick={(task) => setCurrentTask(task)}
				rowHref={(task) =>
					`/${orgSlug}/workspaces/${task.workspaceId}/tasks/${task.id}`
				}
				selectionAction={
					onDeleteTasks
						? {
								label: "Delete",
								icon: <CustomIcon icon={IconTrash} />,
								variant: "destructive",
								onClick: confirmDelete,
								disabled: loading,
							}
						: undefined
				}
				{...props}
			/>
			<TaskDetailsDrawer
				open={!!currentTask}
				onClose={() => setCurrentTask(null)}
				task={currentTask ?? undefined}
			/>
		</>
	);
}
